import {
  Component, Input, Output, EventEmitter,
  forwardRef, signal, computed,
  ChangeDetectionStrategy, ViewEncapsulation,
  ElementRef, inject, OnDestroy, afterNextRender,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';

export interface SelectOption {
  id:        any;
  label:     string;
  disabled?: boolean;
}

export type SelectSize = 'xsmall' | 'small' | 'medium' | 'large' | 'xlarge';

@Component({
  selector:        'app-select',
  standalone:      true,
  imports:         [CommonModule, FormsModule, ReactiveFormsModule],
  templateUrl:     './select.component.html',
  styleUrls:       ['./select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation:   ViewEncapsulation.None,
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => SelectComponent), multi: true }],
})
export class SelectComponent implements ControlValueAccessor, OnDestroy {

  @Input() options:      SelectOption[] = [];
  @Input() placeholder = 'Sélectionner…';
  @Input() disabled    = false;
  @Input() size:         SelectSize = 'medium';
  @Input() addIconLeft  = false;
  @Input() addIconRight = false;

  @Output() selectionChange = new EventEmitter<SelectOption>();

  private readonly _el = inject(ElementRef<HTMLElement>);

  readonly isOpen     = signal(false);
  readonly isFocused  = signal(false);
  readonly selectedId = signal<any>(null);

  readonly selectedOption = computed(() => this.options.find(o => o.id === this.selectedId()) ?? null);
  readonly displayLabel   = computed(() => this.selectedOption()?.label ?? '');

  readonly dropdownStyle = signal<Record<string, string>>({});

  // ── Fermeture au clic extérieur ───────────────────────────────────────────
  private readonly _onDocClick = (e: MouseEvent) => {
    if (!this._el.nativeElement.contains(e.target as Node)) this.isOpen.set(false);
  };

  private readonly _onScroll = () => { if (this.isOpen()) this._updatePos(); };

  constructor() {
    afterNextRender(() => {
      document.addEventListener('click',  this._onDocClick, true);
      window.addEventListener('scroll',   this._onScroll,   true);
      window.addEventListener('resize',   this._onScroll);
    });
  }

  ngOnDestroy(): void {
    document.removeEventListener('click',  this._onDocClick, true);
    window.removeEventListener('scroll',   this._onScroll,   true);
    window.removeEventListener('resize',   this._onScroll);
  }

  // ── CVA ───────────────────────────────────────────────────────────────────
  private _onChange:  (v: any) => void = () => {};
  private _onTouched: () => void       = () => {};

  writeValue(val: any): void                   { this.selectedId.set(val ?? null); }
  registerOnChange(fn: (v: any) => void): void { this._onChange = fn; }
  registerOnTouched(fn: () => void): void      { this._onTouched = fn; }
  setDisabledState(d: boolean): void           { this.disabled = d; }

  // ── Interactions ──────────────────────────────────────────────────────────
  toggle(): void {
    if (this.disabled) return;
    if (!this.isOpen()) { this._updatePos(); this.isOpen.set(true); }
    else this.isOpen.set(false);
  }

  private _updatePos(): void {
    const rect = this._el.nativeElement.getBoundingClientRect();
    this.dropdownStyle.set({ top: `${rect.bottom + 4}px`, left: `${rect.left}px`, width: `${rect.width}px` });
  }

  selectOption(opt: SelectOption): void {
    if (opt.disabled) return;
    this.selectedId.set(opt.id);
    this._onChange(opt.id);
    this._onTouched();
    this.selectionChange.emit(opt);
    this.isOpen.set(false);
  }

  isSelected(opt: SelectOption): boolean { return this.selectedId() === opt.id; }
  onFocus(): void { this.isFocused.set(true); }
  onBlur(): void  { this.isFocused.set(false); this._onTouched(); }
}